import React from 'react';
import 'react-international-phone/style.css';

import {
  Button,
  Center,
  Group,
  Input,
  Loader,
  Select,
  Stack,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { IconCalendar, IconHome, IconTags } from '@tabler/icons-react';
import RegionSelect from 'components/form-fields/auto-complete-fields/RegionSelect';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import useSelfAthlete from 'hooks/athlete/useSelfAthlete';
import useUpdateSelfAthlete from 'hooks/athlete/useUpdateSelfAthlete';
import { PhoneInput } from 'react-international-phone';
import { Address, AthleteWrite, PackageSalesPortalConfiguration, SalesPortalPackageRetrieve } from 'types';
import { CountryEnum } from 'types/enums';
import { requiredPhoneValidator } from 'utils/form-validators';
import { formatPhone, phoneInputCountries } from 'utils/formatters';
import notificationHandler from 'utils/notifications';

// Whether the package's sales-portal configuration asks for any athlete-information
// field. Legacy packages can predate config-row creation, so the config is treated
// as optional — no config means nothing to collect and the step is skipped.
export function hasVisibleAthleteInformationFields(
  config: PackageSalesPortalConfiguration | null | undefined,
): boolean {
  if (!config) return false;
  return (
    config.dobVisible ||
    config.genderVisible ||
    config.addressVisible ||
    config.emergencyNameVisible ||
    config.emergencyPhoneVisible ||
    config.notesVisible
  );
}

interface AthleteInformationFormValues {
  dob: Date | null;
  gender: AthleteWrite['gender'];
  address: Address | null;
  emergencyName: string | null;
  emergencyPhone: string | null;
  medicalNotes: string | null;
}

interface AthleteInformationFormProps {
  selfAthlete: AthleteWrite;
  packageData: SalesPortalPackageRetrieve;
  onContinue: () => void;
}

// The config-driven fields for the signed-in athlete, prefilled from their record
// and saved back to it before the purchase proceeds (spec Item 6 parity: the
// athlete-side portal previously skipped these fields entirely).
const AthleteInformationForm = ({
  selfAthlete,
  packageData,
  onContinue,
}: AthleteInformationFormProps): JSX.Element => {
  const pkgConfig = packageData.packagesalesportalconfiguration;
  const country = packageData.gym.country;
  const { dateFormat } = useLocalization(country);
  const { errorNotification } = notificationHandler();
  const updateSelfAthlete = useUpdateSelfAthlete();

  const form = useForm<AthleteInformationFormValues>({
    initialValues: {
      dob: selfAthlete.dob ? dayjs(selfAthlete.dob).toDate() : null,
      gender: selfAthlete.gender ?? null,
      address: selfAthlete.address ?? null,
      emergencyName: selfAthlete.emergencyName ?? null,
      emergencyPhone: selfAthlete.emergencyPhone ?? null,
      medicalNotes: selfAthlete.medicalNotes ?? null,
    },
    validate: {
      dob: value => (pkgConfig.dobRequired && !value ? 'Date of Birth is required' : null),
      gender: value => (pkgConfig.genderRequired && !value ? 'Gender is required' : null),
      address: value => (pkgConfig.addressRequired && !value ? 'Address is required' : null),
      emergencyName: value =>
        pkgConfig.emergencyNameRequired && !value
          ? 'Emergency Contact name is required'
          : value && value.length > 128
          ? 'Emergency name must be between 1-128 characters'
          : null,
      // SUPP-1359 parity with the public checkout: only format-check emergency phone
      // when shown — a hidden field's stored digits-only value would fail otherwise.
      emergencyPhone: value =>
        !pkgConfig.emergencyPhoneVisible
          ? null
          : pkgConfig.emergencyPhoneRequired && !value
          ? 'Emergency Phone Number is required'
          : value
          ? requiredPhoneValidator(value)
          : null,
      medicalNotes: value => (pkgConfig.notesRequired && !value ? 'Medical Notes is required' : null),
    },
  });

  const handleAddressValueChange = (field: keyof Address, value: string | null): void => {
    form.setFieldValue('address', { country, ...form.values.address, [field]: value } as Address);
  };

  const handleSubmit = (values: AthleteInformationFormValues): void => {
    updateSelfAthlete.mutate(
      {
        ...selfAthlete,
        phone: selfAthlete.phone ? formatPhone(selfAthlete.phone) : null,
        dob: values.dob ? dayjs(values.dob).format('YYYY-MM-DD') : null,
        gender: values.gender ?? null,
        address: values.address as Address,
        emergencyName: values.emergencyName ?? null,
        emergencyPhone: values.emergencyPhone ? formatPhone(values.emergencyPhone) : null,
        medicalNotes: values.medicalNotes ?? null,
      },
      {
        onSuccess: onContinue,
        onError: error => {
          errorNotification('Could not save your information', error.response?.data.message);
        },
      },
    );
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack spacing='xs' px='md'>
        {pkgConfig.dobVisible && (
          <DateInput
            data-cy='dobInput'
            label='Date of Birth'
            placeholder={dateFormat}
            valueFormat={dateFormat}
            clearable
            defaultLevel='decade'
            icon={<IconCalendar />}
            required={pkgConfig.dobRequired}
            {...form.getInputProps('dob')}
          />
        )}

        {pkgConfig.genderVisible && (
          <Select
            data-cy='genderSelectBox'
            label='Gender'
            placeholder='Select Gender'
            icon={<IconTags />}
            data={['Male', 'Female', 'Transgender', 'Non-binary/Non-conforming', 'Prefer not to respond']}
            required={pkgConfig.genderRequired}
            {...form.getInputProps('gender')}
          />
        )}

        {pkgConfig.addressVisible && (
          <>
            <TextInput
              data-cy='addressInput'
              label='Address'
              placeholder='Enter Address'
              icon={<IconHome />}
              required={pkgConfig.addressRequired}
              error={form.getInputProps('address').error}
              value={form.values.address?.addressLines ?? ''}
              onChange={e => handleAddressValueChange('addressLines', e.currentTarget.value)}
            />
            <TextInput
              data-cy='cityInput'
              label='City'
              placeholder='Enter City'
              icon={<IconHome />}
              required={pkgConfig.addressRequired}
              value={form.values.address?.locality ?? ''}
              onChange={e => handleAddressValueChange('locality', e.currentTarget.value)}
            />
            <RegionSelect
              country={country}
              required={pkgConfig.addressRequired}
              icon={<IconHome />}
              value={form.values.address?.region ?? null}
              onChange={value => handleAddressValueChange('region', value)}
              clearable
              byCountryProps={{
                [CountryEnum.USA]: { 'data-cy': 'stateDropdown' },
              }}
            />
            {/* Free-text on purpose (spec Item 6): postal formats are not US-only. */}
            <TextInput
              data-cy='zipcodeInput'
              label='Zip Code'
              placeholder='Enter Zip Code'
              icon={<IconHome />}
              required={pkgConfig.addressRequired}
              maxLength={16}
              value={form.values.address?.zipCode ?? ''}
              onChange={e => handleAddressValueChange('zipCode', e.currentTarget.value)}
            />
          </>
        )}

        {pkgConfig.emergencyNameVisible && (
          <TextInput
            data-cy='emergencyNameInput'
            label='Emergency Contact Name'
            placeholder='Enter Emergency Contact Name'
            icon={<IconTags />}
            required={pkgConfig.emergencyNameRequired}
            {...form.getInputProps('emergencyName')}
            value={form.values.emergencyName ?? ''}
          />
        )}

        {pkgConfig.emergencyPhoneVisible && (
          <Input.Wrapper
            label='Emergency Phone Number'
            required={pkgConfig.emergencyPhoneRequired}
            error={form.getInputProps('emergencyPhone').error}
          >
            <PhoneInput
              defaultCountry={country.toLowerCase()}
              countries={phoneInputCountries}
              placeholder='Enter Emergency Phone Number'
              inputProps={{ autoComplete: 'tel', name: 'emergencyTel' }}
              value={form.values.emergencyPhone ?? ''}
              onChange={value => form.setFieldValue('emergencyPhone', value)}
            />
          </Input.Wrapper>
        )}

        {pkgConfig.notesVisible && (
          <Textarea
            data-cy='medicalNotesInput'
            label='Medical Notes'
            placeholder='Enter Medical Notes'
            required={pkgConfig.notesRequired}
            {...form.getInputProps('medicalNotes')}
            value={form.values.medicalNotes ?? ''}
          />
        )}

        <Group position='right'>
          <Button type='submit' data-cy='athleteInformationContinue' loading={updateSelfAthlete.isLoading}>
            Continue
          </Button>
        </Group>
      </Stack>
    </form>
  );
};

interface AthleteInformationProps {
  packageData: SalesPortalPackageRetrieve;
  onContinue: () => void;
}

const AthleteInformation = ({ packageData, onContinue }: AthleteInformationProps): JSX.Element => {
  const { data: selfAthlete, isLoading, isError } = useSelfAthlete();

  if (isLoading) {
    return (
      <Center my='xl'>
        <Loader />
      </Center>
    );
  }

  if (isError || !selfAthlete?.data) {
    return (
      <Center my='xl'>
        <Text c='dimmed'>Could not load your information. Please try again.</Text>
      </Center>
    );
  }

  return (
    <AthleteInformationForm
      selfAthlete={selfAthlete.data}
      packageData={packageData}
      onContinue={onContinue}
    />
  );
};

export default AthleteInformation;
